import * as Sentry from "@sentry/react";
import type { BrowserOptions, Event } from "@sentry/react";

// `@sentry/react` does not export the Integration type; take it from the option.
type Integration = Extract<
  NonNullable<BrowserOptions["integrations"]>,
  unknown[]
>[number];

const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();
const environment =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
  import.meta.env.MODE;
const release = (import.meta.env.VITE_SENTRY_RELEASE as string | undefined)?.trim();

// The public signature routes carry the link token in the path (/sign/<token>,
// /participant-sign/<token>), and the API calls they make do as well.
const PUBLIC_SIGN_PATH_PATTERN = /^\/(?:participant-)?sign\//i;
const SIGN_TOKEN_SEGMENT_PATTERN = /(\/(?:participant-)?sign\/)[^?#]*/gi;
const REDACTED_SEGMENT = "<redacted>";
// Sentry normalises event data far shallower than this before the hooks run;
// anything nested deeper is replaced instead of being sent unscrubbed.
const MAX_REDACTION_DEPTH = 12;
const DEPTH_LIMIT_PLACEHOLDER = "[Filtered]";

export const isPublicSignPath = (pathname: string) =>
  PUBLIC_SIGN_PATH_PATTERN.test(pathname);

/** Replaces everything after /sign/ or /participant-sign/ up to ?, # or the end. */
export const redactSignatureTokenFromUrl = (value: string) =>
  value.replace(SIGN_TOKEN_SEGMENT_PATTERN, `$1${REDACTED_SEGMENT}`);

const isUnknownArray = (value: unknown): value is readonly unknown[] =>
  Array.isArray(value);

const isPlainObject = (value: object): value is Record<string, unknown> => {
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

// Copies plain objects and arrays with every string redacted. Other objects
// (Date, the SDK's Scope instances) are kept as they are; `copies` maps each
// visited object to its copy, so shared references and cycles survive.
const redactStringsDeep = (
  value: unknown,
  depth: number,
  copies: WeakMap<object, unknown>,
): unknown => {
  if (typeof value === "string") {
    return redactSignatureTokenFromUrl(value);
  }
  if (typeof value !== "object" || value === null) {
    return value;
  }
  const existing = copies.get(value);
  if (existing !== undefined) {
    return existing;
  }
  if (isUnknownArray(value)) {
    if (depth >= MAX_REDACTION_DEPTH) {
      return DEPTH_LIMIT_PLACEHOLDER;
    }
    const copy: unknown[] = [];
    copies.set(value, copy);
    for (const item of value) {
      copy.push(redactStringsDeep(item, depth + 1, copies));
    }
    return copy;
  }
  if (!isPlainObject(value)) {
    return value;
  }
  if (depth >= MAX_REDACTION_DEPTH) {
    return DEPTH_LIMIT_PLACEHOLDER;
  }
  const copy: Record<string, unknown> = {};
  copies.set(value, copy);
  for (const [key, entry] of Object.entries(value)) {
    copy[key] = redactStringsDeep(entry, depth + 1, copies);
  }
  return copy;
};

const redactSignatureTokensDeep = <T>(value: T): T =>
  redactStringsDeep(value, 0, new WeakMap<object, unknown>()) as T;

/**
 * Copy of the event with the sign token redacted from every string in it:
 * request URL and headers, transaction, breadcrumbs, exception values and
 * stack-frame file names, extra, contexts, tags.
 *
 * Sentry 10 also adds the browser's locale, time zone and calendar to every
 * event as `contexts.culture`, which 8.55 never sent. The integration is
 * removed at init (`withoutCultureContext`); dropping the context here as well
 * is defence in depth for the events that do pass through this function.
 */
export const scrubSentryEvent = <T extends Event>(event: T): T => {
  const scrubbed = redactSignatureTokensDeep(event);
  // `scrubbed` and its `contexts` are copies, so this leaves the original alone.
  if (scrubbed.contexts) {
    delete scrubbed.contexts.culture;
  }
  return scrubbed;
};

/**
 * `beforeSendSpan`: covers child spans and the standalone spans (INP) that are
 * sent without passing `beforeSendTransaction`.
 */
export const scrubSentrySpan = <T extends object>(span: T): T =>
  redactSignatureTokensDeep(span);

const CULTURE_CONTEXT_INTEGRATION_NAME = "CultureContext";

/**
 * Sentry 10 installs `CultureContext` as a default integration. It adds the
 * browser's locale, time zone and calendar as `contexts.culture` in
 * `preprocessEvent`, also on the events Session Replay prepares and sends
 * without `beforeSend`; 8.55 sent none of it. So the integration is removed
 * at the source: this is the function form of `integrations`, which has to
 * return the complete list, so it keeps every other default and appends
 * `additions` last, the order the array form gives.
 */
const withoutCultureContext =
  (additions: Integration[]) =>
  (defaults: Integration[]): Integration[] => [
    ...defaults.filter(
      (integration) => integration.name !== CULTURE_CONTEXT_INTEGRATION_NAME,
    ),
    ...additions,
  ];

/** No traces at all while a signature link is open; the configured rate elsewhere. */
export const resolveTracesSampleRate = (
  spanName: string,
  currentPathname: string,
  configuredRate: number,
) =>
  isPublicSignPath(spanName) || isPublicSignPath(currentPathname)
    ? 0
    : configuredRate;

const parseSampleRate = (value: string | undefined, fallback: number) => {
  if (!value) {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

if (dsn) {
  const tracesSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined,
    0.05
  );
  const replaysSessionSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE as string | undefined,
    0
  );
  const replaysOnErrorSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE as string | undefined,
    0.1
  );
  // Signature links are only entered by a full page load. Session Replay
  // records the page, and INP spans copy the page-load route (/sign/<token>)
  // into an attribute and are sent on their own, so neither runs there.
  const loadedOnPublicSignRoute = isPublicSignPath(window.location.pathname);
  const browserTracing = Sentry.browserTracingIntegration({
    enableInp: !loadedOnPublicSignRoute,
  });

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: withoutCultureContext(
      loadedOnPublicSignRoute
        ? [browserTracing]
        : [browserTracing, Sentry.replayIntegration()],
    ),
    tracesSampler: ({ name }) =>
      resolveTracesSampleRate(name, window.location.pathname, tracesSampleRate),
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    beforeSend: scrubSentryEvent,
    beforeSendTransaction: scrubSentryEvent,
    beforeSendSpan: scrubSentrySpan,
    sendDefaultPii: false,
  });

  Sentry.setTag("app", "frontend");
}
